import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { SMOOTHING_ALGORITHMS, SMOOTHING_DEFAULTS } from '@/tools/signalSmoothing/signalSmoothing.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { SmoothingValueWithUnits } from '@/tools/signalSmoothing/signalSmoothing.utilities';
import type { BaseToolPersistedState, ParameterDefinitions, ToolItem, ToolRehydratePayload } from '@/tools/tool.types';

export type SignalSmoothingState = BaseToolPersistedState & {
  /** Signal to smooth; empty until selected. */
  inputSignal?: ToolItem | null;
  /** Color of the output signal and preview. */
  color: string;
  /** Selected smoothing algorithm. */
  algorithmSelectedValue: string;
  /** Sampling period or frequency, including form validity. */
  samplingRate: SmoothingValueWithUnits;
  /** Filter window, or period for downsampling. */
  smoothingWindow: SmoothingValueWithUnits;
  /** Low-pass cutoff period or frequency. */
  cutoff: SmoothingValueWithUnits;
  /** Savitzky-Golay polynomial factor, including uncommitted form input. */
  polynomialFactor: number | string;
  /** Whether the sampling rate is calculated automatically. */
  isSamplingRateAuto: boolean;
  /** Whether the cutoff is calculated automatically. */
  isCutoffAuto: boolean;
  /** Whether exponential smoothing uses tau rather than alpha. */
  isTau: boolean;
  /** Exponential smoothing time constant. */
  tau: SmoothingValueWithUnits;
  /** Exponential smoothing coefficient, including uncommitted form input. */
  alpha: number | string;
  /** Preserves the saved window and warning when editing a legacy low-pass filter. */
  isMigratedLowPassFilter: boolean;
};

export type SignalSmoothingDehydratedState = SignalSmoothingState;
type SignalSmoothingConfigState = Omit<SignalSmoothingState, 'id' | 'name' | 'originalParameters' | 'inputSignal'>;
type SignalSmoothingConfigParams = SignalSmoothingConfigState & {
  type: string;
};

export type SignalSmoothingSettings = Pick<
  SignalSmoothingState,
  | 'color'
  | 'algorithmSelectedValue'
  | 'samplingRate'
  | 'smoothingWindow'
  | 'cutoff'
  | 'polynomialFactor'
  | 'isSamplingRateAuto'
  | 'isCutoffAuto'
  | 'isTau'
  | 'tau'
  | 'alpha'
>;

type SignalSmoothingActions = {
  /** Applies resolved filter settings together, without calculating or previewing. */
  updateSettings: (changes: Partial<SignalSmoothingSettings>) => void;
  setSearchName: (name: string) => void;
  setInputSignal: (item: ToolItem | string | null | undefined) => void;
  setIsMigratedLowPassFilter: (migrated: boolean) => void;
  setAdvancedParametersCollapsedState: (collapsed: boolean) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: ResettableStoreState<SignalSmoothingState>;
};

const parameterDefinitions: ParameterDefinitions<'inputSignal'> = {
  inputSignal: { predicate: ['name', 'inputSignal'] },
};

const initialState: SignalSmoothingState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  inputSignal: undefined,
  algorithmSelectedValue: SMOOTHING_ALGORITHMS.SEEQ_AGILE.VALUE,
  samplingRate: { value: undefined, units: undefined },
  smoothingWindow: { value: undefined, units: undefined },
  cutoff: { value: undefined, units: undefined },
  polynomialFactor: SMOOTHING_DEFAULTS.POLYNOMIAL_FACTOR,
  isSamplingRateAuto: true,
  isCutoffAuto: true,
  isTau: true,
  tau: SMOOTHING_DEFAULTS.TAU,
  alpha: SMOOTHING_DEFAULTS.ALPHA,
  isMigratedLowPassFilter: false,
  color: '',
});

export const dehydrateSignalSmoothingState = (state: SignalSmoothingState): SignalSmoothingDehydratedState => state;

export function getSignalSmoothingConfigParams(state: SignalSmoothingConfigState): SignalSmoothingConfigParams {
  return {
    type: TREND_TOOLS.SIGNAL_SMOOTHING,
    maximumDuration: state.maximumDuration,
    advancedParametersCollapsed: state.advancedParametersCollapsed,
    configVersion: state.configVersion,
    color: state.color,
    algorithmSelectedValue: state.algorithmSelectedValue,
    samplingRate: state.samplingRate,
    smoothingWindow: state.smoothingWindow,
    cutoff: state.cutoff,
    polynomialFactor: state.polynomialFactor,
    isSamplingRateAuto: state.isSamplingRateAuto,
    isCutoffAuto: state.isCutoffAuto,
    isTau: state.isTau,
    tau: state.tau,
    alpha: state.alpha,
    isMigratedLowPassFilter: state.isMigratedLowPassFilter,
  };
}

const signalSmoothingActionsFactory: ActionsFactory<SignalSmoothingState, SignalSmoothingActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),
  setInputSignal: (item) =>
    set((draft) => {
      draft.inputSignal = sanitizeToolItem(typeof item === 'string' ? { id: item } : item);
    }),
  updateSettings: (changes) =>
    set((draft) => {
      Object.assign(draft, changes);
    }),
  setIsMigratedLowPassFilter: (migrated) =>
    set((draft) => {
      draft.isMigratedLowPassFilter = migrated;
    }),
  setAdvancedParametersCollapsedState: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),
  rehydrateForEdit: (payload) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });
      Object.assign(draft, initialState, rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });
    }),
  reset: resetState,
});

const STORE_NAME = 'SignalSmoothing';
const [SignalSmoothingStoreProvider, useSignalSmoothingStoreInstance, useSignalSmoothingStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  signalSmoothingActionsFactory,
);

export { SignalSmoothingStoreProvider };
export const useSignalSmoothingId = () => useSignalSmoothingStoreSelector((state) => state.id);
export const useSignalSmoothingName = () => useSignalSmoothingStoreSelector((state) => state.name);
export const useSignalSmoothingOriginalParameters = () =>
  useSignalSmoothingStoreSelector((state) => state.originalParameters);
export const useSignalSmoothingMaximumDuration = () =>
  useSignalSmoothingStoreSelector((state) => state.maximumDuration);
export const useSignalSmoothingConfigVersion = () => useSignalSmoothingStoreSelector((state) => state.configVersion);
export const useSignalSmoothingInputSignal = () => useSignalSmoothingStoreSelector((state) => state.inputSignal);
export const useSignalSmoothingColor = () => useSignalSmoothingStoreSelector((state) => state.color);
export const useSignalSmoothingAlgorithmSelectedValue = () =>
  useSignalSmoothingStoreSelector((state) => state.algorithmSelectedValue);
export const useSignalSmoothingSamplingRate = () => useSignalSmoothingStoreSelector((state) => state.samplingRate);
export const useSignalSmoothingSmoothingWindow = () =>
  useSignalSmoothingStoreSelector((state) => state.smoothingWindow);
export const useSignalSmoothingCutoff = () => useSignalSmoothingStoreSelector((state) => state.cutoff);
export const useSignalSmoothingPolynomialFactor = () =>
  useSignalSmoothingStoreSelector((state) => state.polynomialFactor);
export const useSignalSmoothingIsSamplingRateAuto = () =>
  useSignalSmoothingStoreSelector((state) => state.isSamplingRateAuto);
export const useSignalSmoothingIsCutoffAuto = () => useSignalSmoothingStoreSelector((state) => state.isCutoffAuto);
export const useSignalSmoothingIsTau = () => useSignalSmoothingStoreSelector((state) => state.isTau);
export const useSignalSmoothingTau = () => useSignalSmoothingStoreSelector((state) => state.tau);
export const useSignalSmoothingAlpha = () => useSignalSmoothingStoreSelector((state) => state.alpha);
export const useSignalSmoothingIsMigratedLowPassFilter = () =>
  useSignalSmoothingStoreSelector((state) => state.isMigratedLowPassFilter);
export const useSignalSmoothingAdvancedParametersCollapsed = () =>
  useSignalSmoothingStoreSelector((state) => state.advancedParametersCollapsed);
export const useSignalSmoothingActions = () => useSignalSmoothingStoreInstance().actions;

/** @deprecated Only for the legacy singleton bridge during Flux migration. */
export function legacyCreateSignalSmoothingStore() {
  return createStoreInstance(initialState, signalSmoothingActionsFactory, { storeName: STORE_NAME });
}

export const usePersistSignalSmoothingStore = () => {
  const register = useRegisterPersistedStore();
  const store = useSignalSmoothingStoreInstance();
  useEffect(
    () =>
      register({
        storeName: 'sqSignalSmoothingStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateSignalSmoothingState,
      }),
    [register, store],
  );
};
